import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { mockProperties, filterProperties } from '../mock/properties';
import { getProperties } from '../services/api';
import PropertyCard from '../components/PropertyCard';
import FilterBar from '../components/FilterBar';
import { Building2, TrendingUp, Users } from 'lucide-react';

const Home = () => {
  const { t } = useTranslation();
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({});

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const data = await getProperties(filters);
        setProperties(data);
      } catch (error) {
        console.error('Error fetching properties:', error);
        // Fallback a mock data si falla el backend
        const filtered = filterProperties(mockProperties, filters);
        setProperties(filtered);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [filters]);

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-blue-900 via-blue-800 to-blue-900 text-white">
        <div className="container mx-auto px-4 py-16">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-5xl font-bold mb-4 font-heading">
              {t('home.hero.title')}
            </h1>
            <p className="text-xl text-blue-100 mb-6 font-body">
              {t('home.hero.subtitle')}
            </p>
            <div className="flex flex-wrap gap-8">
              <div className="flex items-center gap-3">
                <div className="bg-amber-500 p-3 rounded-lg">
                  <Building2 size={28} />
                </div>
                <div>
                  <p className="text-3xl font-bold">{loading ? '...' : properties.length}+</p>
                  <p className="text-blue-200 text-sm">{t('home.hero.properties')}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="bg-amber-500 p-3 rounded-lg">
                  <TrendingUp size={28} />
                </div>
                <div>
                  <p className="text-3xl font-bold">25+</p>
                  <p className="text-blue-200 text-sm">{t('home.hero.experience')}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="bg-amber-500 p-3 rounded-lg">
                  <Users size={28} />
                </div>
                <div>
                  <p className="text-3xl font-bold">800+</p>
                  <p className="text-blue-200 text-sm">{t('home.hero.network')}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Contenido principal */}
      <div className="container mx-auto px-4 py-12">
        {/* Filtros */}
        <FilterBar onFilterChange={handleFilterChange} activeFilters={filters} />

        {/* Resultados */}
        {loading ? (
          <div className="text-center py-20">
            <div className="text-xl text-gray-600">Cargando propiedades...</div>
          </div>
        ) : (
          <>
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-gray-800 font-heading">
                {properties.length} {properties.length === 1 ? 'Propiedad Encontrada' : 'Propiedades Encontradas'}
              </h2>
            </div>

            {/* Grid de propiedades */}
            {properties.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {properties.map(property => (
                  <PropertyCard key={property.id} property={property} />
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="bg-white rounded-xl shadow-md p-12 max-w-md mx-auto">
                  <Building2 className="text-gray-400 mx-auto mb-4" size={64} />
                  <h3 className="text-xl font-bold text-gray-800 mb-2 font-heading">
                    No se encontraron propiedades
                  </h3>
                  <p className="text-gray-600 font-body">
                    Intenta ajustar tus filtros de búsqueda
                  </p>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Call to Action */}
      <div className="bg-gradient-to-r from-amber-500 to-amber-600 py-16 mt-20">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-white mb-4 font-heading">
            {t('home.cta.title')}
          </h2>
          <p className="text-amber-50 text-lg mb-4 max-w-3xl mx-auto font-body">
            {t('home.cta.subtitle')}
          </p>
          <div className="bg-white bg-opacity-20 rounded-lg p-6 mb-8 max-w-3xl mx-auto">
            <p className="text-white text-lg font-semibold mb-2 font-body">
              {t('home.cta.networkTitle')}
            </p>
            <p className="text-amber-50 font-body">
              {t('home.cta.networkDescription')}
            </p>
          </div>
          <div className="flex flex-wrap gap-4 justify-center">
            <a
              href="https://wa.me/5215219841387001"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-white text-amber-600 px-8 py-4 rounded-lg font-bold hover:bg-gray-100 transition-colors shadow-lg hover:shadow-xl font-body"
            >
              {t('home.cta.whatsapp')}
            </a>
            <a
              href="tel:+525219841387001"
              className="bg-blue-900 text-white px-8 py-4 rounded-lg font-bold hover:bg-blue-800 transition-colors shadow-lg hover:shadow-xl font-body"
            >
              {t('home.cta.call')}
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Home;
